
import { createPopper } from '@popperjs/core'
import TraversTree from '@/components/FileViewer/Helpers/TreeTravers'
import DispatchEventUtils from './Mixins/DispatchEventUtils.js'
import Button from './Button.vue'
import TrashIcon from './Icons/TrashIcon.vue'
import MessageChatSquareIcon from './Icons/MessageChatSquareIcon.vue'
import NoteIcon from './Icons/NoteIcon.vue'
import CopyIcon from './Icons/CopyIcon.vue'
import Tippy from './Tippy.vue'
import ScreenUtils from './Mixins/ScreenUtils.js'
import CheckIcon from './Icons/CheckIcon.vue'

export default {
    name: 'HighlightElement',
    components: {
        Button,
        TrashIcon,
        MessageChatSquareIcon,
        NoteIcon,
        CopyIcon,
        CheckIcon,
        Tippy
    },
    mixins: [DispatchEventUtils, ScreenUtils],
    props: {
        canHighlight: Boolean,
        allHighlights: {
            type: Array,
            default: () => []
        },
        indexById: {
            type: Object,
            required: true
        }
    },
    inject: ['meta', 'isLogged', 'toast', 'user', 'fvAPI_HighlightCreate', 'fvAPI_HighlightUpdate', 'fvAPI_HighlightDelete', 'fvAPI_HighlightCopy'],
    data () {
        return {
            showNote: false,
            handleSelectionTimeout: null,
            selectedHighlight: null,
            popper: null,
            note: null,
            noteDebounce: null,
            defaultHighlightColorIndex: 1,
            highlightCopied: false,
            highlightsColors: [
                {
                    color: 'redHighlight',
                    class: 'bg-red-500'
                }, {
                    color: 'yellowHighlight',
                    class: 'bg-yellow-500'
                }, {
                    color: 'blueHighlight',
                    class: 'bg-blue-500'
                }, {
                    color: 'greenHighlight',
                    class: 'bg-green-400'
                }, {
                    color: 'purbleHighlight',
                    class: 'bg-purple-500'
                }
            ]
        }
    },
    computed: {
        shouldShowNote () {
            const { showNote, selectedHighlight, user, note, meta } = this

            return (
                (showNote || selectedHighlight.user?.id !== user?.id) &&
                selectedHighlight.id &&
                (selectedHighlight.user?.id === user?.id || (note && meta?.sharedNotes))
            )
        },
        shouldShowButton () {
            const { selectedHighlight, user } = this

            return (
                !selectedHighlight.user || selectedHighlight.user?.id === user?.id
            )
        },
        title () {
            return this.indexById[this.selectedHighlight.parent_page_id ?? this.selectedHighlight.page_id]?.title || null
        },
        isHighLightOwner () {
            return this.selectedHighlight?.id && this.selectedHighlight.user?.id === this.user?.id
        },
        highlightBoxClasses () {
            if (this.shouldShowNote) { return 'w-[375px]' }
            return this.isPhone() ? 'py-2 px-3.5' : 'py-2 pl-4'
        }
    },
    watch: {
        selectedHighlight (val, old) {
            if (this.selectedHighlight) {
                this.note = this.selectedHighlight.note
                this.$nextTick(() => {
                    this.popper = this.create()
                })
            } else {
                if (old && old.note !== this.note) {
                    old.note = this.note
                    this.saveOrUpdateHighlight(old)
                }
                this.showNote = false
                this.popper.destroy()
            }
        },
        note (val, old) {
            if (this.selectedHighlight && this.selectedHighlight.id && this.selectedHighlight.note !== val && val !== old) {
                const highlight = Object.assign({}, this.selectedHighlight)
                highlight.note = val
                this.saveOrUpdateHighlight(highlight, {
                    isDebounce: true,
                    closeAfter: false,
                    afterCallback: () => {
                        this.selectedHighlight.note = highlight.note
                    }
                })
            }
        }
    },
    mounted () {
        if (!this.canHighlight) {
            return
        }
        this.$parent.$el.addEventListener('pointerdown', this.handleDown)
        this.$parent.$el.addEventListener('pointerup', this.handleSelectionChange)
        this.$parent.$el.addEventListener('pointercancel', this.handleSelectionChange)
    },
    beforeDestroy () {
        if (!this.canHighlight) {
            return
        }
        this.$parent.$el.removeEventListener('pointerdown', this.handleDown)
        this.$parent.$el.removeEventListener('pointerup', this.handleSelectionChange)
        this.$parent.$el.removeEventListener('pointercancel', this.handleSelectionChange)
    },
    methods: {
        popperMounted () {
            if (this.shouldShowNote) {
                this.$refs.noteArea.focus()
            }
        },
        defaultHighlightColor () {
            if (!this.isLogged) {
                this.toast(this.$t('ميزة التحديدات والملاحظات تتطلب تسجيل الدخول'))
                return
            }

            if (this.selectedHighlight.user) {
                this.showNote = !this.showNote
                return
            }

            this.selectedHighlight.color_class = 'yellowHighlight'
            this.saveOrUpdateHighlight(this.selectedHighlight, {
                afterCallback: (highlight) => {
                    this.$nextTick(() => {
                        if (!highlight) {
                            return
                        }

                        const nodes = document.querySelectorAll(`[data-highlight-id="${highlight.id}"]`)
                        if (!nodes.length) {
                            return
                        }

                        const range = new Range()
                        range.setStart(nodes[0].firstChild, 0)
                        range.setEnd(nodes[nodes.length - 1].firstChild, nodes[nodes.length - 1].firstChild.textContent.length - 1)
                        this.selectedHighlight = { ...highlight, el: range }
                        this.showNote = true
                    })
                }
            })
            this.selectedHighlight = null
        },
        handleSaveClick () {
            if (this.note) {
                this.$toast.success(this.$t('تم حفظ الملاحظة'))
            }
            this.selectedHighlight = null
        },
        saveNote (text) {
            if (this.selectedHighlight?.user?.id === this.user?.id) {
                this.note = text
            }
        },
        create () {
            if (this.popper !== null) {
                this.popper.destroy()
            }
            const rootEl = this.getContentContainer(this.selectedHighlight.el.startContainer)

            return createPopper(this.selectedHighlight.el, this.$el, {
                placement: this.isPhone() || this.isTouchDevice() ? 'bottom' : 'top',
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 6]
                        }
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            rootBoundary: rootEl || 'document'
                        }
                    }
                ],
                onFirstUpdate: this.popperMounted,
            })
        },
        copyHighlight () {
            if (!this.isLogged) {
                this.toast(this.$t('ميزة نسخ النص تتطلب تسجيل الدخول'))
                return
            }
            let selectedHighlight = this.selectedHighlight
            if (this.meta.isText) {
                const text = selectedHighlight.text || selectedHighlight.highlighted_text
                navigator.clipboard.writeText(text).then(() => {
                    this.highlightCopied = true
                    setTimeout(() => {
                        this.highlightCopied = false
                    }, 3000)
                }).catch(() => {
                    this.toast(this.$t('فشل في نسخ النص'))
                })
                return
            }
            selectedHighlight = this.updateStartAndEnd(selectedHighlight)

            this.fvAPI_HighlightCopy(selectedHighlight)
            this.selectedHighlight = null
        },
        updateHighlightNote () {
            this.selectedHighlight.note = this.note
            this.saveOrUpdateHighlight(this.selectedHighlight)
            this.selectedHighlight = null
        },
        updateHighlightColor (color, options = {}) {
            if (!this.isLogged) {
                this.toast(this.$t('ميزة التحديدات والملاحظات تتطلب تسجيل الدخول'))
                return
            }
            this.selectedHighlight.color_class = color
            this.saveOrUpdateHighlight(this.selectedHighlight, options)
        },
        deleteHighlight () {
            const selectedHighlight = this.selectedHighlight
            this.fvAPI_HighlightDelete(selectedHighlight)
                .then(() => {
                    const highlightIndex = this.allHighlights.findIndex(s => s.id === selectedHighlight.id)
                    if (highlightIndex !== -1) {
                        this.$emit('delete-highlights', highlightIndex)
                    }
                    this.$toast.success(this.$t('تم حذف التحديد'))
                })
            this.selectedHighlight = null
        },
        saveOrUpdateHighlight (selectedHighlight, {
            isDebounce = false,
            closeAfter = true,
            afterCallback = undefined
        } = {}) {
            selectedHighlight = this.updateStartAndEnd(selectedHighlight)

            const updatePageHighlight = (highlight) => {
                const highlightIndex = this.allHighlights.findIndex(s => s.id === highlight.id)
                if (highlightIndex !== -1) {
                    this.$emit('update-highlights', { highlightIndex, highlight })
                } else {
                    this.$emit('add-highlights', highlight)
                }
                if ('toRemoveIds' in selectedHighlight) {
                    this.$emit('set-highlights', this.allHighlights.filter(h => !selectedHighlight.toRemoveIds.includes(h.id)))
                }
                if (closeAfter) {
                    this.selectedHighlight = null
                }
                if (afterCallback) {
                    afterCallback(highlight)
                }
            }
            const callback = () => {
                if (selectedHighlight.id) {
                    this.fvAPI_HighlightUpdate(selectedHighlight).then(updatePageHighlight)
                } else {
                    this.fvAPI_HighlightCreate(selectedHighlight).then(updatePageHighlight)
                }
            }

            clearTimeout(this.noteDebounce)
            if (isDebounce) {
                this.noteDebounce = setTimeout(callback, 1500)
            } else {
                callback()
            }
        },
        isSelectionEmpty () {
            const selection = document.getSelection()

            return selection.type !== 'Range' || !selection.toString().trim().length
        },
        updateStartAndEnd (selectedHighlight) {
            if (!this.meta.isText) {
                selectedHighlight.start_id = selectedHighlight.el.startContainer.nodeName === 'SPAN'
                    ? selectedHighlight.el.startContainer.closest('[data-is-word]').id
                    : selectedHighlight.el.startContainer.parentElement.closest('[data-is-word]').id
                selectedHighlight.end_id = selectedHighlight.el.endContainer.nodeName === 'SPAN'
                    ? selectedHighlight.el.endContainer.closest('[data-is-word]').id
                    : selectedHighlight.el.endContainer.parentElement.closest('[data-is-word]').id
            }

            return selectedHighlight
        },
        isSelectionOutsideContent () {
            const range = document.getSelection().getRangeAt(0)

            const rangeStartContainer = this.getContentContainer(range.startContainer)
            const rangeEndContainer = this.getContentContainer(range.endContainer)

            return !rangeStartContainer || !rangeEndContainer
        },
        getContentContainer (el) {
            return this.getHtmlEl(el).closest('[data-content-container], [data-regulation-content-container], [data-explanation-content-container]')
        },
        getHtmlEl (el) {
            return el.nodeName === '#text' ? el.parentElement : el
        },
        handleDown (ev) {
            if (this.selectedHighlight && !ev.target.closest('[data-highlight]')) {
                this.selectedHighlight = null
                return
            }

            this.checkHighlightClicked(ev)
        },
        handleSelectionChange (ev) {
            clearTimeout(this.handleSelectionTimeout)
            this.handleSelectionTimeout = setTimeout(() => {
                const selection = document.getSelection()

                if (selection.type !== 'Range' ||
                    !this.canHighlight ||
                    (this.selectedHighlight &&
                        this.selectedHighlight.id &&
                        (this.getHtmlEl(selection.getRangeAt(0).startContainer)
                            .closest('[data-highlight]') ||
                            this.getHtmlEl(selection.getRangeAt(0).startContainer)
                                .closest('[data-highlight]')
                        )
                    )
                ) {
                    return
                }
                if (this.isSelectionEmpty() || this.isSelectionOutsideContent()) {
                    this.selectedHighlight = null
                    return
                }
                this.expandRangeToWords()
                const range = selection.getRangeAt(0)

                let start = null
                let end = null
                const text = range.toString()
                const toRemoveIds = new Set()
                const sameNode = range.startContainer.isSameNode(range.endContainer)
                TraversTree.walkInTree((el, startOffset) => {
                    if (this.getHtmlEl(range.startContainer).closest('[data-highlight]') || this.getHtmlEl(range.endContainer).closest('[data-highlight]')) {
                        return false
                    }
                    const sameStartContainer = range.startContainer.nodeName === '#text' ? el.isSameNode(range.startContainer) : el.parentNode.isSameNode(range.startContainer)
                    const sameEndContainer = range.endContainer.nodeName === '#text' ? el.isSameNode(range.endContainer) : el.parentNode.isSameNode(range.endContainer)
                    if (sameNode) {
                        if (sameStartContainer && start === null) {
                            if (range.startContainer.nodeName === '#text') {
                                start = startOffset + range.startOffset
                            } else {
                                start = startOffset
                            }
                            if (range.endContainer.nodeName === '#text') {
                                end = startOffset + range.endOffset
                            } else {
                                end = startOffset + el.textContent.length
                            }
                            if (el.parentNode.nodeName === 'MARK') {
                                toRemoveIds.add(el.parentNode.dataset.highlightId)
                            }
                        }
                        return
                    }

                    if (sameStartContainer && start === null) {
                        if (range.startContainer.nodeName === '#text') {
                            start = startOffset + range.startOffset
                        } else {
                            start = startOffset
                        }
                        if (el.parentNode.nodeName === 'MARK') {
                            toRemoveIds.add(el.parentNode.dataset.highlightId)
                        }
                    } else if (sameEndContainer && end === null) {
                        if (range.endContainer.nodeName === '#text') {
                            end = startOffset + range.endOffset
                        } else {
                            end = startOffset + el.textContent.length
                        }
                        if (el.parentNode.nodeName === 'MARK') {
                            toRemoveIds.add(el.parentNode.dataset.highlightId)
                        }
                    } else if (start !== null && end === null) {
                        if (el.parentNode.nodeName === 'MARK') {
                            toRemoveIds.add(el.parentNode.dataset.highlightId)
                        }
                    }
                }, this.getContentContainer(range.startContainer), '#text')

                if (start === null || end === null) {
                    this.selectedHighlight = null
                    return
                }

                const container = this.getContentContainer(range.startContainer)
                if (!container) {
                    return
                }
                const hash = container.dataset.hash
                const pageId = parseInt(container.dataset.pageId)
                const contextId = container.dataset.contextId ? parseInt(container.dataset.contextId) : undefined
                const contextType = container.dataset.contextType
                const locale = container.dataset.locale

                this.selectedHighlight = {
                    hash,
                    page_id: pageId,
                    locale,
                    context_id: contextId,
                    context_type: contextType,
                    color_class: null,
                    start_id: Math.min(start, end),
                    end_id: Math.max(start, end),
                    toRemoveIds: [...toRemoveIds].map(id => parseInt(id)),
                    text,
                    note: '',
                    el: range
                }
            }, 150)
        },
        expandRangeToWords () {
            const selection = window.getSelection()

            if (!selection.rangeCount) { return } // If there's no selection, exit

            // Get the first and last range
            const range = selection.getRangeAt(0)
            const startContainer = range.startContainer
            const endContainer = range.endContainer

            // Expand the start
            const startNode = startContainer
            let startOffset = range.startOffset

            // If the start is a text node
            if (startNode.nodeType === Node.TEXT_NODE) {
                const text = startNode.textContent
                while (startOffset > 0 && /\p{L}/gu.test(text[startOffset - 1])) {
                    startOffset--
                }
            }

            // Expand the end
            const endNode = endContainer
            let endOffset = range.endOffset

            // If the end is a text node
            if (endNode.nodeType === Node.TEXT_NODE) {
                const text = endNode.textContent
                while (endOffset < text.length && /\p{L}/gu.test(text[endOffset])) {
                    endOffset++
                }
            }

            // Now create a new range from the expanded offsets
            const newRange = document.createRange()
            newRange.setStart(startNode, startOffset)
            newRange.setEnd(endNode, endOffset)

            // Clear current selection and apply the new range
            selection.removeAllRanges()
            selection.addRange(newRange)
        },
        getLastTextNode (node) {
            if (node.nodeType === Node.TEXT_NODE) { return node }
            let last = node.lastChild
            while (last && last.nodeType !== Node.TEXT_NODE) {
                last = last.lastChild
            }
            return last
        },
        checkHighlightClicked (ev) {
            const target = ev.target
            const isMark = target.nodeName === 'MARK'
            const isUnderline = target.nodeName === 'MARK_UNDERLINE'
            const isInsideMark = target.parentNode?.nodeName === 'MARK'

            if (!isMark && (!isUnderline || !isInsideMark)) { return }

            const highlightId = isUnderline
                ? target.parentNode.dataset.highlightId
                : target.dataset.highlightId

            const highlight = this.allHighlights.find(h => h.id == highlightId)
            if (!highlight) { return }

            const nodes = document.querySelectorAll(`[data-highlight-id="${highlight.id}"]`)
            if (!nodes.length) { return }

            const startNode = this.getLastTextNode(nodes[0])
            const endNode = this.getLastTextNode(nodes[nodes.length - 1])

            if (!startNode || !endNode) { return }

            const range = new Range()
            range.setStart(startNode, 0)
            range.setEnd(endNode, endNode.textContent.length)

            this.selectedHighlight = { ...highlight, el: range }

            ev.preventDefault()
            ev.stopPropagation()
        },
        isTouchDevice () {
            return (('ontouchstart' in window) ||
                (navigator.maxTouchPoints > 0) ||
                (navigator.msMaxTouchPoints > 0))
        }
    }
}
